
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Edit, Shield, Trash2, MoreHorizontal, Globe } from "lucide-react";
import type { Database } from "@/integrations/supabase/types";

type UserProfile = Database['public']['Tables']['profiles']['Row'];

interface UserTableRowProps {
  user: UserProfile;
  onEdit: (user: UserProfile) => void;
  onVerify: (user: UserProfile) => void;
  onDelete?: (user: UserProfile) => void;
  onManageCountries?: (user: UserProfile) => void;
}

const UserTableRow = ({ 
  user, 
  onEdit, 
  onVerify, 
  onDelete, 
  onManageCountries 
}: UserTableRowProps) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'admin':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'pollster':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'media':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getStatusBadgeColor = (verified: boolean) => {
    return verified 
      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
  };

  return (
    <tr className="border-b dark:border-gray-700">
      <td className="px-4 py-3">
        <div className="font-medium text-gray-900 dark:text-gray-100">
          {user.name || 'Unknown'}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="text-sm text-gray-600 dark:text-gray-300">
          {user.email}
        </div>
      </td>
      <td className="px-4 py-3">
        <Badge className={getRoleBadgeColor(user.role)}>
          {user.role?.replace('_', ' ').toUpperCase()}
        </Badge>
      </td>
      <td className="px-4 py-3">
        <Badge className={getStatusBadgeColor(user.verified || false)}>
          {user.verified ? 'Verified' : 'Pending'}
        </Badge>
      </td>
      <td className="px-4 py-3">
        <div className="text-sm text-gray-600 dark:text-gray-300">
          {user.country_code || '-'}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="text-sm text-gray-600 dark:text-gray-300">
          {user.constituency_code || '-'}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="text-sm text-gray-600 dark:text-gray-300">
          {formatDate(user.created_at)}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(user)}
            className="text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white"
          >
            <Edit className="h-4 w-4 mr-1" />
            Edit
          </Button>
          
          {!user.verified && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onVerify(user)}
              className="text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100"
            >
              <Shield className="h-4 w-4 mr-1" />
              Verify
            </Button>
          )}
          
          {(user.role === 'admin' || user.role === 'super_admin') && onManageCountries && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onManageCountries(user)}
              className="text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-100 border-green-300 dark:border-green-600 hover:border-green-500 dark:hover:border-green-400"
            >
              <Globe className="h-4 w-4 mr-1" />
              Countries
            </Button>
          )}
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onDelete && (
                <DropdownMenuItem
                  onClick={() => onDelete(user)}
                  className="text-red-600 dark:text-red-400"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete User
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  );
};

export default UserTableRow;
